import Highlighter from "@intility/bifrost-react-highlighter";
import "@intility/bifrost-react-highlighter/highlighter.css";
import Box from "@intility/bifrost-react/Box";
import Message from "@intility/bifrost-react/Message";
import Tabs from "@intility/bifrost-react/Tabs";
import type { ReactNode } from "react";
import { Fragment } from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import { Link } from "react-router";
import rehypeSlug from "rehype-slug";
import remarkGfm from "remark-gfm";
import { DOC_SECTIONS, type DocSectionKey } from "./content";
import { DocTree } from "./DocTree";
import { MermaidDiagram } from "./MermaidDiagram";
import "./markdown.css";

const GITHUB_BLOB = "https://github.com/intility/intility-id/blob/main";

const LANG_ALIAS: Record<string, string> = { ts: "typescript" };

const SECTION_DIR: Record<DocSectionKey, string[]> = {
  "getting-started": ["docs", "getting-started"],
  concepts: ["docs", "concepts"],
  identity: ["docs", "identity"],
  authorization: ["docs", "authorization"],
  "workload-identity": ["docs", "workload-identity"],
  reference: ["docs", "reference"],
};

const DOC_DIR_MAP = new Map<string, DocSectionKey>();
for (const sec of DOC_SECTIONS) {
  DOC_DIR_MAP.set(SECTION_DIR[sec.key].join("/"), sec.key);
}

function resolveDir(base: string[], href: string): string[] {
  const [path] = href.split("#");
  const segments = [...base];
  const parts = path.split("/");
  parts.slice(0, -1).forEach((part) => {
    if (part === "" || part === ".") return;
    if (part === "..") segments.pop();
    else segments.push(part);
  });
  return segments;
}

function toGithubUrl(base: string[], href: string): string {
  const [path, hash] = href.split("#");
  const segments = [...base];
  for (const part of path.split("/")) {
    if (part === "" || part === ".") continue;
    if (part === "..") segments.pop();
    else segments.push(part);
  }
  return `${GITHUB_BLOB}/${segments.join("/")}${hash ? `#${hash}` : ""}`;
}

function slugFromHref(href: string): string {
  const [path] = href.split("#");
  const filename = path.split("/").pop() ?? "";
  return filename.replace(/\.md$/i, "");
}

function makeDocLink(section: DocSectionKey) {
  return function DocLink({
    href,
    children,
  }: {
    href?: string;
    children?: ReactNode;
  }) {
    if (!href) return <>{children}</>;

    const hashAnchor = href.includes("#") ? `#${href.split("#")[1]}` : "";

    if (href.startsWith("#")) return <a href={href}>{children}</a>;

    // A bare relative *.md href ("authorization-api.md") is the dotted form
    // without the "./", so route it through the same resolution instead of
    // treating it as an external URL. A trailing "#anchor" is the only
    // suffix slugFromHref understands, so a query string stays external.
    const bareRelativeMd =
      !/^[a-z][a-z0-9+.-]*:/i.test(href) &&
      !href.startsWith("/") &&
      /\.md(#|$)/i.test(href);

    if (href.startsWith(".") || bareRelativeMd) {
      const base = SECTION_DIR[section];
      const targetDir = resolveDir(base, href);
      const targetSection = DOC_DIR_MAP.get(targetDir.join("/"));
      if (targetSection) {
        const slug = slugFromHref(href);
        return (
          <Link to={`/docs/${targetSection}/${slug}${hashAnchor}`}>
            {children}
          </Link>
        );
      }
      return (
        <a href={toGithubUrl(base, href)} target="_blank" rel="noreferrer">
          {children}
        </a>
      );
    }

    return (
      <a href={href} target="_blank" rel="noreferrer">
        {children}
      </a>
    );
  };
}

const baseComponents: Components = {
  pre: ({ children }) => <div className="md-codeblock">{children}</div>,
  code: ({ className, children }) => {
    const text = String(children ?? "");
    const match = /language-(\w+)/.exec(className ?? "");

    if (match) {
      if (match[1] === "tree") return <DocTree source={text} />;
      if (match[1] === "mermaid")
        return <MermaidDiagram source={text.replace(/\n$/, "")} />;

      const lang = LANG_ALIAS[match[1]] ?? match[1];
      return (
        <Highlighter language={lang}>{text.replace(/\n$/, "")}</Highlighter>
      );
    }

    if (text.includes("\n")) {
      return (
        <pre className="md-pre">
          <code>{text.replace(/\n$/, "")}</code>
        </pre>
      );
    }

    return <code className="md-code">{children}</code>;
  },
  table: ({ children }) => (
    <div className="md-table-wrap">
      <table>{children}</table>
    </div>
  ),
};

/**
 * A `<!-- tabs -->` … `<!-- /tabs -->` block renders as Bifrost tabs, one tab
 * per `### heading` inside it. The markers are HTML comments, so GitHub shows
 * the same content as plain headings.
 */
const TABS_BLOCK = /<!--\s*tabs\s*-->([\s\S]*?)<!--\s*\/tabs\s*-->/g;

const TAB_CONTENT_PROPS = { style: { padding: "4px 20px 16px" } };

function splitTabs(block: string): { label: string; body: string }[] {
  // split() with a capturing group yields [before, label, body, label, body, …]
  const parts = block.split(/^###[ \t]+(.+)$/m);
  const tabs: { label: string; body: string }[] = [];
  for (let i = 1; i < parts.length - 1; i += 2) {
    tabs.push({ label: parts[i].trim(), body: parts[i + 1] });
  }
  return tabs;
}

/**
 * A `<!-- box -->` … `<!-- /box -->` block renders its content (plain
 * paragraphs, not a list) inside a Bifrost `Box` — the "text box" component
 * from Figma, used for a short set of definitions that reads better as prose
 * than as bullets. The markers are HTML comments, so GitHub shows the plain
 * paragraphs underneath.
 */
const BOX_BLOCK = /<!--\s*box\s*-->([\s\S]*?)<!--\s*\/box\s*-->/g;

/**
 * An `<!-- env development -->` … `<!-- /env -->` block (or `env production`)
 * renders only in the Portal built for that environment. The same Markdown is
 * baked into both Portals, so this is how a page says "you are in the
 * development Portal" without forking the file. GitHub shows both variants as
 * plain paragraphs.
 *
 * By default the block renders as a Bifrost `Message` callout. Write
 * `<!-- env production plain -->` to render the body as ordinary page content
 * instead: use `plain` when the variant holds real section content, such as a
 * command or a `tabs` block, rather than a note.
 *
 * Do not put headings inside an env block. The ToC extractor reads the raw
 * source, so it would count both variants while the page renders one.
 */
const ENV_BLOCK =
  /<!--\s*env\s+(development|production)(?:\s+(plain))?\s*-->([\s\S]*?)<!--\s*\/env\s*-->/g;

export const PORTAL_ENVIRONMENT: "development" | "production" =
  import.meta.env.VITE_ENVIRONMENT === "production"
    ? "production"
    : "development";

interface Block {
  type: "tabs" | "box" | "env";
  start: number;
  end: number;
  body: string;
  /** Only for `env` blocks: the environment the block is for. */
  environment?: "development" | "production";
  /** Only for `env` blocks: render as ordinary content, not a `Message`. */
  plain?: boolean;
}

function findBlocks(source: string): Block[] {
  const blocks: Block[] = [];
  for (const match of source.matchAll(TABS_BLOCK)) {
    blocks.push({
      type: "tabs",
      start: match.index ?? 0,
      end: (match.index ?? 0) + match[0].length,
      body: match[1],
    });
  }
  for (const match of source.matchAll(BOX_BLOCK)) {
    blocks.push({
      type: "box",
      start: match.index ?? 0,
      end: (match.index ?? 0) + match[0].length,
      body: match[1],
    });
  }
  for (const match of source.matchAll(ENV_BLOCK)) {
    blocks.push({
      type: "env",
      start: match.index ?? 0,
      end: (match.index ?? 0) + match[0].length,
      body: match[3],
      environment: match[1] as "development" | "production",
      plain: match[2] !== undefined,
    });
  }
  blocks.sort((a, b) => a.start - b.start);
  // A block inside another block is rendered by the outer block already.
  return blocks.filter(
    (block, i) => !blocks.some((other, j) => j < i && other.end > block.start),
  );
}

/**
 * Render a Markdown source, processing `tabs` / `box` / `env` blocks. Block
 * bodies go through this same function, so blocks nest: a plain `env` block
 * can hold a `tabs` block, a `Message` callout can hold a `box`, and so on.
 */
function renderBlocks(md: string, section: DocSectionKey): ReactNode {
  const components: Components = {
    ...baseComponents,
    a: makeDocLink(section),
  };

  const render = (text: string) => (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[rehypeSlug]}
      components={components}
    >
      {text}
    </ReactMarkdown>
  );

  const pieces: ReactNode[] = [];
  let cursor = 0;
  for (const block of findBlocks(md)) {
    if (block.start > cursor) {
      pieces.push(
        <div key={`md-${cursor}`}>{render(md.slice(cursor, block.start))}</div>,
      );
    }
    if (block.type === "tabs") {
      const tabs = splitTabs(block.body);
      if (tabs.length > 0) {
        pieces.push(
          <Tabs key={`tabs-${block.start}`} contentBackground="base-2">
            {tabs.map((tab) => (
              <Tabs.Item
                key={tab.label}
                content={renderBlocks(tab.body, section)}
                contentProps={TAB_CONTENT_PROPS}
              >
                {tab.label}
              </Tabs.Item>
            ))}
          </Tabs>,
        );
      }
    } else if (block.type === "env") {
      if (block.environment === PORTAL_ENVIRONMENT) {
        pieces.push(
          block.plain ? (
            <Fragment key={`env-${block.start}`}>
              {renderBlocks(block.body, section)}
            </Fragment>
          ) : (
            <Message
              key={`env-${block.start}`}
              state={block.environment === "production" ? "success" : "warning"}
            >
              {renderBlocks(block.body, section)}
            </Message>
          ),
        );
      }
    } else {
      pieces.push(
        <Box
          key={`box-${block.start}`}
          background="base-2"
          radius="m"
          padding={16}
        >
          {renderBlocks(block.body, section)}
        </Box>,
      );
    }
    cursor = block.end;
  }
  if (cursor < md.length) {
    pieces.push(<div key={`md-${md.length}`}>{render(md.slice(cursor))}</div>);
  }

  return <div className="md-body">{pieces}</div>;
}

export function Markdown({
  source,
  section,
}: {
  source: string;
  section: DocSectionKey;
}) {
  return renderBlocks(source, section);
}
